import { BrowserRouter } from "react-router-dom";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router";
import { useEffect, useState } from "react";
import { authAccessToken } from "@service/auth";
import { getCookie, removeCookie } from "@util/cookie";
import GuestRoute from "./guest_route";
import LoginPage from "@page/login";
import ForgotPasswordPage from "@page/forgot-password";
import ResetPasswordPage from "@page/reset-password";
import EmployerSignup from "@page/employer-signup";
import OuraJoinPage from "@page/oura-join";
import JobseekerSignup from "@page/jobseeker-signup";
import ProtectedRoute from "./protected_route";
import JobSeekerContract from "@page/job-seeker-dashboard/contract";
import JobSeekerMyJob from "@page/job-seeker-dashboard/my-job";
import ProfileReview from "@page/your-profile";
import AiInterview from "@page/ai-interview";
import ApplyNow from "@page/apply-now";
import CandidateInterviewPage from "@page/candidate-interview";
import ProfileCompletionPage from "@page/profile-completion";
import EmployerJobPage from "@page/employer-jobs";
import EmployerJoApplicationPage from "@page/employer-job-applications";
import EmployerSuggestedCandidate from "@page/employer-suggested-candidate";
import EmployerHirePage from "@page/employer-hires";
import EmployerAssessPage from "@page/employer-assess";
import CandidateProfilePage from "@page/employer-candidate-profile";
import NotFoundPage from "@page/not-found";
import DashboardRoute from "./dashboard_route";
import DashboardPage from "@page/dashboard";
import UnAuthorizedPage from "@page/unauthorized";
import RoleRoute from "./role_route";
import SettingsPage from "@page/setting";
import OuraRoute from "./oura_route";
import OuraWorkspace, { OuraHomeRedirect } from "@page/oura-workspace";
import { OURA_ROOT } from "@oura/nav";
import { DELIVERY_ON } from "@oura/delivery/flag";
import { DOCS_WRITE_ON } from "@oura/docs/writeFlag";
import { FORM_ANSWERS_ON } from "@service/aeo/formResponses";
import { REQUESTS_ON } from "@oura/requests/flag";
import OuraReportRender from "@page/oura-report-render";
import OuraScanDraft from "@page/oura-scan-draft";
import { SCAN_DRAFT_UI_ON } from "@oura/newscan/flags";
import JobDetailPage from "@page/job-detail/job-detail";
import PaymentPage from "@page/payment";
import CandidateInterviewAssessmentPage from "@page/candidate-interview-assessment";
import FirstMoveInterview from "@page/first-move-interview";
import FirstMoveInterviewDemo from "@page/first-move-interview-demo";
import CandidateInterviewFeedbackPage from "@page/candidate-interview-feedback";
import CandidateInterviewFeedbackDetailPage from "@page/candidate-interview-detail";
import CandidateInterviewBrowsePage from "@page/candidate-interview-browse";
import CandidateInterviewBrowseAuthPage from "@page/candidate-interview-browse/auth";
import StartupCandidateProfile from "@page/startup-candidate-profile";
import EmployerVerifyTeam from "@page/employer-verify-team";
import ShortLinkRedirectPage from "@page/short-link-redirect";
import { resolveWaShortLink } from "@service/short-link";
import EmployerCreateJobPage from "@page/employer-create-job";
import EmployerEditJobPage from "@page/employer-edit-job";
import AIInterviewDemo from "@page/ai-interview-demo";
import AIInterviewDemoCall from "@page/ai-interview-demo-call";
import StartAIInterviewDemo from "@page/start-ai-interview-demo";
import PracticeInterview from "@page/practice-interview";
import EmployerAssessFormEditPage from "@page/employer-assess-form-edit";
import EmployerAssessInterviewEditPage from "@page/employer-assess-interview-edit";
import EmployerAssessFormCreatePage from "@page/employer-assess-form-create";
import EmployerAssessInterviewCreatePage from "@page/employer-assess-interview-create";
import EmployerSubmissionPage from "@page/employer-submission";
import DemoInterviewPage from "@page/demo-interview";
import DemoInterviewCreatePage from "@page/demo-interview-create";
import DemoInterviewEditPage from "@page/demo-interview-edit";
import InterviewInsightDemo from "@page/interview-insight-demo";
import SubdomainProvider from "@context/subdomain_context";
import { getCurrentSubdomain } from "@util/subdomain";
import EmployerApproveCollaboration from "@page/employer-approve-collaboration";
import RequestJoinTeamPage from "@page/employer-owner-approve-join";
import JobseekerQuickApply from "@page/jobseeker-quick-apply";
import GeneralInterviewV2 from "@page/general-interview/general-interview-v2";
import ApproveAssessmentLinkPage from "@page/approve-assessment-link";
import ApplyNowV2 from "@page/apply-now/apply-v2";
import BulkUploadProgress from "@container/bulk-upload-progress";
import { BulkUploadCVProvider } from "@context/bulk_upload_cv_context";
import { AiqmUploadProvider } from "@context/aiqm_upload_context";
import AiqmProcessingToast from "@container/aiqm-processing-toast";
import OuraProcessingToast from "@container/oura-processing-toast";
import { KbUploadProvider } from "@context/kb_upload_context";
import KbProcessingToast from "@container/kb-processing-toast";
import WorkspaceListPage from "@page/workspace-list";
import { WorkspaceCreatePage } from "@page/workspace-create";
import { WorkspaceEditPage } from "@page/workspace-edit";
import ApproveActivationWorkspace from "@page/approve-activation-workspace";
import WorkspaceSetupPage from "@page/workspace-setup";
import SwitchWorkspacePage from "@page/switch-workspace";
import EmployerBillingPage from "@page/employer-billing";
import { SubscriptionProvider } from "@context/subscription_context";
import AgentCallsPage from "@page/agent-calls";
import AgentCallDetailPage from "@page/agent-call-detail";
import AgentCallSetupPage from "@page/agent-call-setup";
import AgentCallSetupNewPage from "@page/agent-call-setup-new";
import AgentCallSetupEditPage from "@page/agent-call-setup-edit";
import AgentSettingsPage from "@page/agent-settings";
import AdminCandidateInvestigatorPage from "@page/admin-candidate-investigator";
import AdminCandidateInvestigatorDetailPage from "@page/admin-candidate-investigator/detail";
import AdminHiringActivityPage from "@page/admin-hiring-activity";
import AdminSchedulerTestPage from "@page/admin-scheduler-test";
import AdminSubscriptionPackagesPage from "@page/admin-subscription-packages";
import AdminUsersPage from "@page/admin-users";
import MayaChatSimulatorPage from "@page/maya-chat-simulator";
import TrainingSessionPage from "@page/training-session";
import TrainingResultsPage from "@page/training-results";
import TrainingCampaignListPage from "@page/training-campaign-list";
import TrainingCampaignCreatePage from "@page/training-campaign-create";
import TrainingCampaignDetailPage from "@page/training-campaign-detail";
import TrainingPerformancePage from "@page/training-performance";
import ReviewerVerifyPage from "@page/reviewer/verify";
import ReviewerCandidatePage from "@page/reviewer/candidates";
import ReviewerActivatePage from "@page/reviewer/activate";
import TrainingAiqmPage from "@page/training-aiqm";
import TrainingAiqmDetailPage from "@page/training-aiqm-detail";
import SubscriptionPaymentPage from "@page/subscription-payment";
import TrainingKnowledgeBasePage from "@page/training-knowledge-base";

// The Knowledge Base list/upload/delete backend is not wired yet, so this route
// stays gated behind a flag until the service calls exist (the nav entry in
// src/containers/layout/training-auth.tsx is gated on the same flag).
const kbEnabled = import.meta.env.VITE_ENABLE_TRAINING_KB === "true";

function CandidateRouteSwitch() {
    // Logged-in HR/admin → existing employer candidate profile.
    // Otherwise (reviewer with REVIEWER_TOKEN, or no cookies at all) →
    // reviewer view; the page itself routes unauthenticated visitors to OTP.
    if (getCookie("authToken")) {
        return <CandidateProfilePage />;
    }
    return <ReviewerCandidatePage />;
}

/**
 * /oura/schedule became /oura/work: the Work view carries everything Schedule
 * showed. The query and the hash ride along, so a link into the old page still
 * lands on the same thing.
 */
function OuraScheduleRedirect() {
    const { search, hash } = useLocation();
    return <Navigate to={{ pathname: `${OURA_ROOT}/work`, search, hash }} replace />;
}

/**
 * Delivery replaced Fixes and folded in Plan (30 Sep 2026): a link into
 * Fixes lands on Delivery, the Contract on its Plan view, the Timeline on
 * By month. Only where Delivery is served; elsewhere the old screens stay.
 */
function OuraDeliveryRedirect({ view }: { view?: "plan" | "month" }) {
    const { businessId } = useParams();
    const { hash } = useLocation();
    const search = view ? `?view=${view}` : "";
    return <Navigate to={{ pathname: `${OURA_ROOT}/businesses/${businessId ?? ""}/delivery`, search, hash }} replace />;
}

/**
 * Delivery itself, only where it is served. Elsewhere a saved link to it lands
 * on the screen it replaces there: its Plan view on the Contract, By month on
 * the Timeline, anything else on Fixes.
 */
function OuraDeliveryRoute() {
    const { businessId } = useParams();
    const { search, hash } = useLocation();
    if (DELIVERY_ON) return <OuraWorkspace screen="delivery" />;
    const view = new URLSearchParams(search).get("view");
    const screen = view === "plan" ? "contract" : view === "month" ? "timeline" : "todo";
    return <Navigate to={{ pathname: `${OURA_ROOT}/businesses/${businessId ?? ""}/${screen}`, hash }} replace />;
}

/**
 * The New scan page (draft the 50 before the paid scan), only where its switch
 * is on (VITE_ENABLE_OURA_SCAN_DRAFT, exactly "true"). Off, both of its URLs
 * land on the businesses list and the New scan modal stays the way in.
 */
function OuraScanDraftRoute({ mode }: { mode: "new" | "business" }) {
    if (!SCAN_DRAFT_UI_ON) return <Navigate to={`${OURA_ROOT}/businesses`} replace />;
    return <OuraScanDraft mode={mode} />;
}

function ActivateUser({ onInterviewComplete }: { onInterviewComplete: (route: string) => void }) {
    const { token } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const activateAccount = async () => {
            try {
                // Send the token to your backend for verification
                await authAccessToken(token as string).then((res) => {
                    onInterviewComplete(res.onInterviewComplete);
                    removeCookie("__apply_job_guest__");
                    sessionStorage.removeItem("cv_uuid");
                    if (import.meta.env.VITE_MODE_DEMO_PRACTICE_INTERVIEW === "true") {
                        sessionStorage.removeItem("supporting_files_uuids");
                    }
                    return res;
                }).then((res) => {
                    navigate(res.targetRoute || "/ai-interview");
                    return res;
                });

                // Optionally, you can redirect the user to the target route
            } catch (err) {
                if (err instanceof Error && 'response' in err) {
                    const errorResponse = (err as any).response;
                    setError(errorResponse?.data?.message || "Activation failed.");
                } else {
                    setError("Activation failed.");
                }
            } finally {
                setIsLoading(false);
            }
        };

        activateAccount();
    }, [token]);

    if (isLoading) {
        return <div>Activating your account...</div>;
    }

    if (error) {
        return <div>Error: {error}</div>;
    }

    return <div>Account activated successfully!</div>;
}

function MainRoutes() {
    const [onInterviewComplete, setOnInterviewComplete] = useState<string>("");
    // The workspace subdomain, or null on one of our own reserved hosts (app /
    // www / staging / localhost, and "link" — the WhatsApp short-link
    // redirector, #2935; without that exemption SubdomainProvider fetches a
    // company called "link", 404s, and takes the whole app over with
    // NotFoundPage). The reserved list lives in @util/subdomain so a second
    // caller can't copy it and drift from what the router actually resolves.
    const company = getCurrentSubdomain();

    return (
        <BrowserRouter>
            <AiqmUploadProvider>
            <KbUploadProvider>
            <BulkUploadCVProvider>
                <SubdomainProvider subdomain={company || ""}>
                    <SubscriptionProvider>
                        <Routes>
                            <Route path="/job/:jobid" element={<JobDetailPage />} />
                            <Route path="/demo-call/:companyname" element={<AIInterviewDemoCall />} />
                            <Route path="/demo/:companyname" element={<AIInterviewDemo />} />
                            <Route path="/demo/:companyname/interview" element={<StartAIInterviewDemo />} />
                            <Route path="/demo/:companyname/insight/:conversation_id" element={<InterviewInsightDemo />} />
                            <Route path="/firstmove-vibecheck" element={<FirstMoveInterviewDemo />} />
                            <Route path="/firstmove-vibecheck/interview" element={<FirstMoveInterview routeTo="/dashboard" />} />
                            <Route path="/interview/browse" element={<CandidateInterviewBrowsePage />} />
                            {/* WhatsApp short-link redirect (link.ourteam.ai/s/<code>) —
                                public, no-login candidate flow (#2935). */}
                            <Route path="/s/:code" element={<ShortLinkRedirectPage />} />
                            {/* WhatsApp AD short link (link.ourteam.ai/wa/<code>) — public
                                marketing redirect to wa.me (#3269, BE #4012). */}
                            <Route path="/wa/:code" element={<ShortLinkRedirectPage resolve={resolveWaShortLink} />} />
                            {/* #3494: the route the PDF renderer prints. PUBLIC on purpose —
                                headless Chromium opens it with no session, so any guard could
                                only ever block the renderer. The credential is the BE's
                                short-lived render token (scoped to one assessment id, ~5 min
                                TTL, refused for any other id or route by require_aeo_caller);
                                with an invalid, expired or mismatched token the fetch returns
                                nothing and the page renders nothing printable. */}
                            <Route path="/oura/report/:assessmentId" element={<OuraReportRender />} />
                            <Route path="/verify/team" element={<EmployerVerifyTeam />} />
                            {/* A merchant's colleague accepting an Oura invite: public, the token is the invite. */}
                            <Route path="/oura/join" element={<OuraJoinPage />} />
                            <Route path="/verify/approve" element={<EmployerApproveCollaboration />} />
                            <Route path="/verify/approve-join" element={<RequestJoinTeamPage />} />
                            <Route path="/verify/approve-assessment-link" element={<ApproveAssessmentLinkPage />} />
                            <Route path="/verify/activated-workspace" element={<ApproveActivationWorkspace />} />
                            <Route path="/workspace/setup" element={<WorkspaceSetupPage />} />
                            <Route path="/switch-workspace" element={<SwitchWorkspacePage />} />

                            <Route element={<GuestRoute />}>
                                <Route path="/" element={<LoginPage />} />
                                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                                <Route path="/employer-signup" element={<EmployerSignup />} />
                                <Route path="/candidate-signup" element={<JobseekerSignup />} />
                                <Route path="/quick-apply/:jobid" element={<JobseekerQuickApply />} />
                                <Route path="/activate/:token" element={<ActivateUser onInterviewComplete={setOnInterviewComplete} />} />
                                <Route path="/ai-interview" element={<GeneralInterviewV2 />} />
                                {/* Candidate screening flow. /screening-interview is the canonical
                                    path for newly generated links; /assessment-interview is kept
                                    indefinitely so links already sent to candidates keep working. */}
                                <Route path="/screening-interview/:job_id" element={<ApplyNowV2 />} />
                                <Route path="/assessment-interview/:job_id" element={<ApplyNowV2 />} />
                            </Route>

                            {/* Reviewer isolated pages — no platform chrome, no auth required */}
                            <Route path="/reviewer/verify" element={<ReviewerVerifyPage />} />
                            <Route path="/reviewer/activate" element={<ReviewerActivatePage />} />

                            {/*
                              Unified candidate route — same URL used by HR (CandidateProfilePage)
                              and external reviewers (ReviewerCandidatePage). Kept outside of
                              ProtectedRoute/RoleRoute so reviewers (no authToken) aren't redirected.
                            */}
                            <Route path="/candidate/:candidateid" element={<CandidateRouteSwitch />} />

                            <Route path="/training-session/:token" element={<TrainingSessionPage />} />
                            <Route path="/training-results/:token" element={<TrainingResultsPage />} />

                            <Route element={<ProtectedRoute />}>
                                <Route element={<DashboardRoute />}>
                                    <Route path="/dashboard" element={<DashboardPage />} />
                                </Route>
                                <Route path="/setting" element={<SettingsPage />} />

                                {/*
                                  * Shared Subscription & Payment surface — ONE
                                  * reusable surface for the Stripe subscription
                                  * epic, used by recruiter (hr/admin/super_admin),
                                  * training-agent, and agent. The page is
                                  * platform-aware (resolves the platform from
                                  * role/company_type), renders its own layout per
                                  * platform, and is gated behind
                                  * VITE_STRIPE_SUBSCRIPTION_ENABLED (flag off ⇒
                                  * the page redirects to /dashboard, so the route
                                  * is a no-op until the epic ships).
                                  */}
                                <Route element={<RoleRoute allowedRoles={["hr", "admin", "super_admin", "training-agent", "agent"]} />}>
                                    <Route path="/subscription" element={<SubscriptionPaymentPage />} />
                                    {/* Billing is platform-aware (recruiter + training): the page
                                        picks its own layout (AuthLayout / TrainingAuthLayout). */}
                                    <Route path="/billing" element={<EmployerBillingPage />} />
                                </Route>
                                <Route element={<RoleRoute allowedRoles={["job_seeker"]} />}>
                                    <Route path="/contracts" element={<JobSeekerContract />} />
                                    <Route path="/my-jobs" element={<JobSeekerMyJob />} />
                                    <Route path="/resume" element={<ProfileReview />} />
                                    <Route path="/ai-interview/:interviewid" element={(
                                        <AiInterview
                                            routeTo={onInterviewComplete}
                                            interviewType="voice"
                                            showLayout={true}
                                        />
                                    )} />
                                    <Route path="/ai-interview/vietnamese" element={(
                                        <AiInterview
                                            routeTo={onInterviewComplete}
                                            interviewType="voice"
                                            showLayout={true}
                                        />
                                    )} />
                                    <Route path="/apply/:jobid" element={<ApplyNow />} />
                                    <Route path="/browse" element={<CandidateInterviewBrowseAuthPage />} />
                                    <Route path="/interview" element={<CandidateInterviewPage />} />
                                    <Route path="/interview/assessment" element={<CandidateInterviewAssessmentPage />} />
                                    <Route path="/interview/:interviewid" element={<CandidateInterviewAssessmentPage />} />
                                    <Route path="/interview/assessment" element={<CandidateInterviewAssessmentPage />} />
                                    <Route path="/interview/:interviewid/feedback" element={<CandidateInterviewFeedbackPage />} />
                                    <Route path="/interview/:interviewid/detail" element={<CandidateInterviewFeedbackDetailPage />} />
                                    <Route path="/interview/:interviewid/start" element={<PracticeInterview />} />
                                    <Route path="/profile/completion" element={<ProfileCompletionPage />} />
                                    <Route path="/payment" element={<PaymentPage />} />
                                </Route>


                                <Route element={<RoleRoute allowedRoles={["agent"]} />}>
                                    <Route path="/agent/calls" element={<AgentCallsPage />} />
                                    <Route path="/agent/calls/:id" element={<AgentCallDetailPage />} />
                                    <Route path="/agent/call-setup" element={<AgentCallSetupPage />} />
                                    <Route path="/agent/call-setup/new" element={<AgentCallSetupNewPage />} />
                                    <Route path="/agent/call-setup/:id" element={<AgentCallSetupEditPage />} />
                                    <Route path="/agent/settings" element={<AgentSettingsPage />} />
                                </Route>

                                <Route element={<RoleRoute allowedRoles={["hr", "admin", "super_admin"]} />}>
                                    <Route path="/jobs" element={<EmployerJobPage />} />
                                    <Route path="/workspaces" element={<WorkspaceListPage />} />
                                    <Route path="/workspaces/create" element={<WorkspaceCreatePage />} />
                                    <Route path="/workspaces/:company_id/edit" element={<WorkspaceEditPage />} />
                                    <Route path="/demo" element={<DemoInterviewPage />} />
                                    <Route path="/demo/create" element={<DemoInterviewCreatePage />} />
                                    <Route path="/demo/:alias/edit" element={<DemoInterviewEditPage />} />
                                    <Route path="/jobs/create" element={<EmployerCreateJobPage />} />
                                    <Route path="/jobs/edit/:jobid" element={<EmployerEditJobPage />} />
                                    <Route path="/jobs/:jobid/applications" element={<EmployerJoApplicationPage />} />
                                    <Route path="/jobs/:jobid/candidates" element={<EmployerSuggestedCandidate />} />
                                    <Route path="/hires" element={<EmployerHirePage />} />
                                    <Route path="/assess" element={<EmployerAssessPage />} />
                                    <Route path="/assess/create/form" element={<EmployerAssessFormCreatePage />} />
                                    <Route path="/assess/create/interview" element={<EmployerAssessInterviewCreatePage />} />
                                    <Route path="/assess/:assessment_id/form" element={<EmployerAssessFormEditPage />} />
                                    <Route path="/assess/:assessment_id/:job_id/submissions" element={<EmployerSubmissionPage />} />
                                    <Route path="/assess/:assessment_id/interview" element={<EmployerAssessInterviewEditPage />} />
                                    <Route path="/startup/:startup_submission_id" element={<StartupCandidateProfile />} />
                                </Route>

                                {/*
                                  * "training-agent" is an existing role string
                                  * already used by src/pages/dashboard/index.tsx
                                  * (role-switch landing) and
                                  * src/containers/layout/training-auth.tsx
                                  * (sidebar nav). Roles are typed as plain
                                  * strings throughout the codebase (see
                                  * src/types/auth.ts), so there is no enum to
                                  * extend. Client-side role gating is advisory
                                  * — backend RBAC is the source of truth for
                                  * write actions like /training/campaigns/new.
                                  */}
                                <Route element={<RoleRoute allowedRoles={["hr", "admin", "super_admin", "training-agent"]} />}>
                                    <Route path="/training/campaigns" element={<TrainingCampaignListPage />} />
                                    <Route path="/training/campaigns/new" element={<TrainingCampaignCreatePage />} />
                                    <Route path="/training/campaigns/:id" element={<TrainingCampaignDetailPage />} />
                                    <Route path="/training/performance" element={<TrainingPerformancePage />} />
                                    {/* Legacy redirects — keep old URLs working with the new sub-tab pre-selected */}
                                    <Route path="/training/people" element={<Navigate to="/training/performance?tab=employees" replace />} />
                                    <Route path="/training/reports" element={<Navigate to="/training/performance?tab=team" replace />} />
                                    <Route path="/training/aiqm" element={<TrainingAiqmPage />} />
                                    <Route path="/training/aiqm/:id" element={<TrainingAiqmDetailPage />} />
                                    {kbEnabled && (
                                        <Route path="/training/knowledge-base" element={<TrainingKnowledgeBasePage />} />
                                    )}
                                </Route>

                                <Route element={<RoleRoute allowedRoles={["admin", "super_admin"]} />}>
                                    <Route path="/candidate-investigator" element={<AdminCandidateInvestigatorPage />} />
                                    <Route path="/candidate-investigator/:candidateId" element={<AdminCandidateInvestigatorDetailPage />} />
                                    <Route path="/hiring-activity" element={<AdminHiringActivityPage />} />
                                    <Route path="/scheduler-test" element={<AdminSchedulerTestPage />} />
                                    <Route path="/admin/subscription-packages" element={<AdminSubscriptionPackagesPage />} />
                                    <Route path="/admin/users" element={<AdminUsersPage />} />
                                    <Route path="/maya-chat-simulator" element={<MayaChatSimulatorPage />} />
                                </Route>

                                {/*
                                  * Oura — the local-business AI-visibility product. Two roles:
                                  * "oura-client" (their own single business) and "oura-staff"
                                  * (the growth-team pipeline that drills into any business). The
                                  * backend does not emit these roles yet, so OuraRoute allows
                                  * previewing in dev/staging and is strict only in production.
                                  * See handoff-docs for the BE contract this anticipates.
                                  */}
                                <Route element={<OuraRoute />}>
                                    <Route path="/oura" element={<OuraHomeRedirect />} />
                                    {/* Staff roll-up home */}
                                    <Route path="/oura/overview" element={<OuraWorkspace screen="overview" />} />
                                    {/* Client: their own business lives at the root */}
                                    <Route path="/oura/dashboard" element={<OuraWorkspace screen="dashboard" />} />
                                    {/* Client approval layer: the merchant's decisions and reports. */}
                                    <Route path="/oura/decisions" element={<OuraWorkspace screen="decisions" />} />
                                    <Route path="/oura/reports" element={<OuraWorkspace screen="reports" />} />
                                    <Route path="/oura/work" element={<OuraWorkspace screen="work" />} />
                                    <Route path="/oura/schedule" element={<OuraScheduleRedirect />} />
                                    <Route path="/oura/questions" element={<OuraWorkspace screen="questions" />} />
                                    <Route path="/oura/search" element={<OuraWorkspace screen="search" />} />
                                    <Route path="/oura/setup" element={<OuraWorkspace screen="setup" />} />
                                    {/* Client: the access they give us during onboarding */}
                                    <Route path="/oura/access" element={<OuraWorkspace screen="access" />} />
                                    {/* Client: who can sign in to their account */}
                                    <Route path="/oura/team" element={<OuraWorkspace screen="team" />} />
                                    <Route path="/oura/todo" element={<OuraWorkspace screen="todo" />} />
                                    <Route path="/oura/report" element={<OuraWorkspace screen="report" />} />
                                    {/* Staff: the pipeline, and drill-in per business */}
                                    <Route path="/oura/businesses" element={<OuraWorkspace screen="businesses" />} />
                                    {/* Staff: AEO Assessment prescreen/create/poll/report (#3065) */}
                                    <Route path="/oura/assessment" element={<OuraWorkspace screen="assessment" />} />
                                    {/* Staff: the Skill library (Docs phase 2). Only where the Docs write switch is on; elsewhere the list. */}
                                    <Route path="/oura/skills" element={DOCS_WRITE_ON ? <OuraWorkspace screen="skills" /> : <Navigate to={`${OURA_ROOT}/businesses`} replace />} />
                                    {/* Staff: every onboarding form answer and whether it arrived (#4065). Sample data until its routes ship, so production waits for the flag. */}
                                    <Route path="/oura/form-answers" element={FORM_ANSWERS_ON ? <OuraWorkspace screen="formAnswers" /> : <Navigate to={`${OURA_ROOT}/businesses`} replace />} />
                                    {/* Staff: the Tracker, every client's delivery on one page, wherever Delivery is served. */}
                                    <Route path="/oura/tracker" element={DELIVERY_ON ? <OuraWorkspace screen="board" /> : <Navigate to={`${OURA_ROOT}/businesses`} replace />} />
                                    {/* Staff: every client's change requests, from any channel. Sample data until its routes ship, so production waits for the flag. */}
                                    <Route path="/oura/requests" element={REQUESTS_ON ? <OuraWorkspace screen="requests" /> : <Navigate to={`${OURA_ROOT}/businesses`} replace />} />
                                    <Route path="/oura/businesses/new" element={<OuraScanDraftRoute mode="new" />} />
                                    <Route path="/oura/businesses/:businessId/questions/draft" element={<OuraScanDraftRoute mode="business" />} />
                                    <Route path="/oura/businesses/:businessId" element={<OuraWorkspace screen="dashboard" />} />
                                    <Route path="/oura/businesses/:businessId/questions" element={<OuraWorkspace screen="questions" />} />
                                    <Route path="/oura/businesses/:businessId/search" element={<OuraWorkspace screen="search" />} />
                                    <Route path="/oura/businesses/:businessId/setup" element={<OuraWorkspace screen="setup" />} />
                                    <Route path="/oura/businesses/:businessId/pages" element={<OuraWorkspace screen="pages" />} />
                                    <Route path="/oura/businesses/:businessId/documents" element={<OuraWorkspace screen="documents" />} />
                                    <Route path="/oura/businesses/:businessId/knowledge" element={<OuraWorkspace screen="knowledge" />} />
                                    <Route path="/oura/businesses/:businessId/connectors" element={<OuraWorkspace screen="connectors" />} />
                                    <Route path="/oura/businesses/:businessId/todo" element={DELIVERY_ON ? <OuraDeliveryRedirect /> : <OuraWorkspace screen="todo" />} />
                                    <Route path="/oura/businesses/:businessId/delivery" element={<OuraDeliveryRoute />} />
                                    <Route path="/oura/businesses/:businessId/reviews" element={<OuraWorkspace screen="reviews" />} />
                                    <Route path="/oura/businesses/:businessId/contract" element={DELIVERY_ON ? <OuraDeliveryRedirect view="plan" /> : <OuraWorkspace screen="contract" />} />
                                    <Route path="/oura/businesses/:businessId/timeline" element={DELIVERY_ON ? <OuraDeliveryRedirect view="month" /> : <OuraWorkspace screen="timeline" />} />
                                    <Route path="/oura/businesses/:businessId/activity" element={<OuraWorkspace screen="activity" />} />
                                    <Route path="/oura/businesses/:businessId/history" element={<OuraWorkspace screen="history" />} />
                                    <Route path="/oura/businesses/:businessId/onboarding" element={<OuraWorkspace screen="onboarding" />} />
                                    <Route path="/oura/businesses/:businessId/report" element={<OuraWorkspace screen="report" />} />
                                    {/* Settings is the shared /setting page (see pages/setting) */}
                                </Route>
                            </Route>

                            <Route path="/unauthorized" element={<UnAuthorizedPage />} />
                            <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                    </SubscriptionProvider>
                </SubdomainProvider>
                <BulkUploadProgress />
                <AiqmProcessingToast />
                <KbProcessingToast />
                {/* Oura scan progress. Self-gates to /oura/* routes, so it never
                    renders (or polls AEO endpoints) on Recruiter/Trainer. */}
                <OuraProcessingToast />
            </BulkUploadCVProvider>
            </KbUploadProvider>
            </AiqmUploadProvider>
        </BrowserRouter>

    )
}

export default MainRoutes;